/* eslint-disable eqeqeq */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { getOperationDetails, useCommon } from '@facephi/inphinite-common';
import {
  Button,
  CardBaseFooter,
  FlexContainer,
  Label,
  Switch,
  useTimezone,
} from '@facephi/ui-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ModalReject } from './ModalReject';
import { ModalReview } from './ModalReview';
import { ReviewCardListElement, ReviewTextArea } from './Styles';
import {
  CardBaseContent,
  CardBaseStyle,
} from '../../components/CardDashboard/Styles';
import { useOperationDetails } from '../../hooks';
import {
  OperationStatus,
  ReviewDto,
  StatusMessages,
  reviewSchema,
} from '../../state/model';
import { createReview } from '../../state/mutations';

export const ReviewForm = () => {
  const { t } = useTranslation();
  const [showModalAccepted, setModalAccepted] = useState<boolean>(false);
  const [showModalRejected, setModalRejected] = useState<boolean>(false);
  const [lastResult, setLastResult] = useState<string>();
  const { id } = useParams<{ id: string }>() as { id: string };
  const { formatTimezone } = useTimezone();

  const {
    control,
    handleSubmit,
    register,
    formState: { errors, isValid },
    reset,
  } = useForm({
    resolver: yupResolver(reviewSchema),
    defaultValues: {
      sediaData: false,
      debtData: false,
      evidenceData: false,
      observations: '',
    },
  });

  const {
    tenant: { id: tenantId },
  } = useCommon();
  const [saveReview] = useMutation(createReview);

  const { detailsData } = useOperationDetails(id, tenantId);

  useEffect(() => {
    const details = detailsData?.details;
    if (details) {
      const changeList = details?.statusChangeList || [];

      if (changeList.length > 0) {
        const dataList: string[] = [];
        changeList.forEach((element) => {
          (element?.status === OperationStatus.succeeded ||
            element?.status === OperationStatus.denied) &&
            dataList.push(
              `${t(StatusMessages[element?.status])} ${
                element?.timestamp ? formatTimezone(element?.timestamp) : ''
              }`,
            );
        });
        setLastResult(dataList.pop() || '');
      }
    }
  }, [detailsData]);

  useEffect(() => reset(), [showModalRejected]);

  const onSubmit = async (data: ReviewDto) => {
    const response = await saveReview({
      variables: {
        input: {
          checks: [
            {
              name: 'sediaData',
              isChecked: true,
            },
            {
              name: 'debtData',
              isChecked: true,
            },
            {
              name: 'evidenceData',
              isChecked: true,
            },
          ],
          comment: data.observations,
          approved: true,
        },
        operationId: id,
      },
      refetchQueries: [
        {
          query: getOperationDetails,
          variables: {
            operationId: id,
            tenantId,
          },
        },
      ],
    });

    if (response.data) {
      setModalAccepted(true);
      reset();
    }
  };

  return (
    <>
      <CardBaseStyle flexDirection="column" testId="card-review">
        <FlexContainer
          as="form"
          flexDirection="column"
          flex="1"
          onSubmit={handleSubmit(onSubmit)}
        >
          <CardBaseContent flexDirection="column" rowGap="1.6">
            <ReviewCardListElement
              alignItems="center"
              justifyContent="space-between"
              forwardedAs="li"
            >
              <Label fontSize="12">{t('Has SEDIA data been reviewed?')}</Label>
              <Controller
                name="sediaData"
                control={control}
                render={({ field: { value, ...field } }) => (
                  <Switch {...field} checked={value} testId="sedia-input" />
                )}
              />
            </ReviewCardListElement>
            <ReviewCardListElement
              alignItems="center"
              justifyContent="space-between"
              forwardedAs="li"
            >
              <Label fontSize="12">
                {t('Has International debt been reviewed?')}
              </Label>
              <Controller
                name="debtData"
                control={control}
                render={({ field: { value, ...field } }) => (
                  <Switch {...field} checked={value} testId="debt-input" />
                )}
              />
            </ReviewCardListElement>
            <ReviewCardListElement
              alignItems="center"
              justifyContent="space-between"
              forwardedAs="li"
            >
              <Label fontSize="12">{t('Have evidences been reviewed?')}</Label>
              <Controller
                name="evidenceData"
                control={control}
                render={({ field: { value, ...field } }) => (
                  <Switch {...field} checked={value} testId="evidence-input" />
                )}
              />
            </ReviewCardListElement>
            <FlexContainer alignItems="center" justifyContent="space-between">
              <ReviewTextArea
                label={t('Observations')}
                {...register('observations')}
                rows={10}
                iconRight="PencilSimpleLine"
                testId="observations-input"
                errorLabel={
                  errors.observations?.message && t(errors.observations.message)
                }
              />
            </FlexContainer>
            <Label black semibold fontSize="12">
              {lastResult}
            </Label>
          </CardBaseContent>
          <CardBaseFooter as="section" columnGap="1.6" hasPadding>
            <Button
              onClick={() => setModalRejected(true)}
              color="error"
              testId="review-reject"
            >
              {t('Reject')}
            </Button>
            <Button type="submit" testId="review-approve" disabled={!isValid}>
              {t('Approve')}
            </Button>
          </CardBaseFooter>
        </FlexContainer>
      </CardBaseStyle>
      <ModalReview
        onChangeShow={(showModalAccepted) =>
          setModalAccepted(showModalAccepted)
        }
        show={showModalAccepted}
        onCreate={() => {}}
        onClose={() => setModalAccepted(false)}
      />
      <ModalReject
        onChangeShow={(showModalRejected) =>
          setModalRejected(showModalRejected)
        }
        show={showModalRejected}
        operationId={id}
      />
    </>
  );
};
